"use client";
import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { QueueMinusCircleIcon, QueuePlusCircleIcon } from "@/components/icons";

function TicketBannerTrack({
  texts,
  diamondClassName,
  textClassName,
  reverse = false,
}: {
  texts: string[];
  diamondClassName: string;
  textClassName: string;
  reverse?: boolean;
}) {
  const repeatedTexts = [...texts, ...texts];

  return (
    <div
      className="animate-scroll flex w-max min-w-[200%] items-center gap-5 pl-2"
      style={{
        animationDirection: reverse ? "reverse" : "normal",
      }}
    >
      {repeatedTexts.map((str, index) => (
        <div
          className="flex w-fit shrink-0 flex-row items-center gap-2"
          key={`${str}-${index}`}
        >
          <div className={`h-2 w-2 rotate-45 ${diamondClassName}`} />
          <span
            className={`font-bold uppercase text-[20px] ${textClassName}`}
            style={{ marginLeft: 8 }}
          >
            {str}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function Tickets() {
  const [activeSlide, setActiveSlide] = useState(0);
  const [isAgeRatingOpen, setIsAgeRatingOpen] = useState(false);
  const [stateCode, setStateCode] = useState("");
  const sliderRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setStateCode(window.localStorage.getItem("state") || "");
  }, []);

  const handleTicketScroll = () => {
    const slider = sliderRef.current;

    if (!slider) {
      return;
    }

    const nextSlide = Math.round(slider.scrollLeft / slider.clientWidth);
    setActiveSlide(nextSlide);
  };

  const scrollToTicket = (index: number) => {
    const slider = sliderRef.current;

    if (!slider) {
      return;
    }

    slider.scrollTo({
      left: slider.clientWidth * index,
      behavior: "smooth",
    });
    setActiveSlide(index);
  };

  return (
    <>
      <section
        className="reveal-up"
        id="ingressos"
        style={{
          paddingBottom: 20,
          backgroundColor: "#fff",
        }}
      >
        <div className="flex flex-col justify-center pl-5">
          <div
            className="flex justify-start reveal-scale"
            style={{ marginTop: 40 }}
          >
            <div className="">
              <h1 className="font-extrabold text-[#2033ef] text-[34px] uppercase">
                INGRESSOS
              </h1>
            </div>
          </div>
          <br />
          <p
            className="reveal-up"
            style={{
              animationDelay: "0.1s",
              color: "#000",
              width: "100%",
              maxWidth: 500,
              paddingRight: 40,
            }}
          >
            <b>Escolha como você quer viver a experiência da Casa CazéTV.</b>
            <br />
            <br />
            Cada ingresso oferece uma forma diferente de viver a Copa do Mundo
            da FIFA 2026™, sempre do nosso jeito.
          </p>

          <div
            className="age-rating-accordion reveal-up"
            style={{ animationDelay: "0.15s" }}
          >
            <button
              type="button"
              className="age-rating-trigger"
              aria-expanded={isAgeRatingOpen}
              aria-controls="tickets-age-rating-content"
              onClick={() => setIsAgeRatingOpen((current) => !current)}
            >
              <span>Classificação etária</span>
              <span className="age-rating-icon">
                <QueuePlusCircleIcon
                  className={`absolute inset-0 h-full w-full transition-all duration-300 ease-out ${
                    isAgeRatingOpen
                      ? "rotate-90 scale-75 opacity-0"
                      : "rotate-0 scale-100 opacity-100"
                  }`}
                />
                <QueueMinusCircleIcon
                  className={`absolute inset-0 h-full w-full transition-all duration-300 ease-out ${
                    isAgeRatingOpen
                      ? "rotate-0 scale-100 opacity-100"
                      : "-rotate-90 scale-75 opacity-0"
                  }`}
                />
              </span>
            </button>

            <div
              id="tickets-age-rating-content"
              className={`age-rating-panel ${isAgeRatingOpen ? "is-open" : ""}`}
            >
              <div className="age-rating-panel-inner">
                <p>
                  - Menores até 15 anos*: Entrada permitida apenas se
                  acompanhados por responsável legal ou adulto autorizado. Será
                  necessário apresentar o{" "}
                  <span
                    className="age-rating-link"
                    onClick={() =>
                      window.open(
                        "https://drive.google.com/file/d/18bfYdTARxlYRx4LbPP0OmROUC3FtOq_Y/view",
                      )
                    }
                  >
                    Termo de Declaração de Responsabilidade para Menores
                    Acompanhados
                  </span>
                  , devidamente preenchido e assinado.
                </p>

                <p>
                  - Menores de 16 e 17 anos*: Poderão acessar o evento
                  desacompanhados, desde que apresentem o{" "}
                  <span
                    className="age-rating-link"
                    onClick={() =>
                      window.open(
                        "https://drive.google.com/file/d/1wfnnDNs2dmwU1kR00g-xyRu3CgWIxKOC/view?usp=sharing",
                      )
                    }
                  >
                    Termo de Autorização para Entrada de Menores Desacompanhados
                  </span>
                  , assinado por um responsável legal.
                </p>

                <p>
                  * A classificação etária acima poderá ser alterada,
                  restringida ou ampliada conforme determinação constante do
                  alvará judicial expedido pela Vara da Infância e da Juventude
                  competente ou por decisão de autoridade competente.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div
          ref={sliderRef}
          onScroll={handleTicketScroll}
          className="tickets-container reveal-up"
          style={{ animationDelay: "0.2s", paddingBottom: 60 }}
        >
          {/* Pista Ticket */}
          <div
            className="ticket-card pista reveal-up border-white"
            style={{
              animationDelay: "0.25s",
              borderWidth: 2,
            }}
          >
            <div
              className="ticket-content text-white flex flex-col gap-2.5"
              style={{ backgroundColor: "#2033ef" }}
            >
              <Image
                src="/images/geral2.png"
                alt="Ticket Pista"
                height={150}
                width={320}
                quality={75}
                priority
              />
              <p className="text-[18px]">
                Pra quem quer viver tudo, <br />
                colado na galera.
              </p>
              <ul className="ticket-features">
                <li>Telão de última geração com som imersivo</li>
                <li>Acesso a todas as ativações da Casa</li>
                <li>
                  Circulação livre entre pista e arquibancada (sem assentos
                  marcados)
                </li>
                <li>Acesso ao food park</li>
              </ul>

              <div style={{ marginTop: 16 }} />

              <div style={{ marginTop: 8 }} />

              <a
                onClick={() =>
                  window.open(
                    `https://www.sympla.com.br/casacazetv?tracking=SITE_HOME_INGRESSOS_GERAL_${stateCode}`,
                  )
                }
                className="flex justify-center cursor-pointer"
              >
                <Image
                  src="/images/comprar.svg"
                  alt="Ticket Cativa"
                  height={190}
                  width={150}
                  quality={75}
                  priority
                />
              </a>
            </div>
          </div>

          {/* Lounge Ticket */}
          <div
            className="ticket-card-cativa lounge reveal-up border-white"
            style={{
              animationDelay: "0.4s",
              borderWidth: 2,
              backgroundColor: "#2033ef",
              display: stateCode === "SP" ? "" : "none",
            }}
          >
            <div className="ticket-content text-white flex flex-col gap-2.5">
              <Image
                src="/images/cativa.png"
                alt="Ticket Cativa"
                height={190}
                width={350}
                quality={75}
                priority
              />
              <p className="text-[18px]">
                Mais conforto pra curtir cada momento, sem perder a emoção
              </p>
              <div className="ticket-badge bg-[#F45E76]" style={{ width: 290 }}>
                Todos os benefícios do GERAL, mais:
              </div>
              <ul className="ticket-features">
                <li>Acesso ao Mezanino</li>
                <li>Vista privilegiada do telão</li>
                <li>Open bar de água, refrigerante e cerveja</li>
                {/*<li>Banheiros exclusivos</li>*/}
              </ul>
              <div style={{ marginTop: 48 }} />
              {/*<span className="text-sm text-white text-left italic">
                *Desconto e parcelamento válido para compra de ingressos com
                cartão Itaú Visa realizadas entre 29/04 a 05/05.
              </span>*/}
              <div style={{ marginTop: 12 }} />
              <a
                onClick={() =>
                  window.open(
                    `https://www.sympla.com.br/casacazetv?tracking=SITE_HOME_INGRESSOS_CATIVA_${stateCode}`,
                  )
                }
                target="_blank"
                className="flex justify-center cursor-pointer"
              >
                <Image
                  src="/images/comprar.svg"
                  alt="Ticket Cativa"
                  height={190}
                  width={150}
                  quality={75}
                  priority
                />
              </a>
            </div>
          </div>
        </div>

        <div
          className="ticket-dots"
          aria-label="Navegacao dos ingressos"
          style={{
            marginTop: 30,
          }}
        >
          {[0, 1].map((index) => (
            <button
              key={index}
              type="button"
              className={`ticket-dot ${activeSlide === index ? "is-active" : ""}`}
              aria-label={`Ir para o ingresso ${index + 1}`}
              aria-pressed={activeSlide === index}
              onClick={() => scrollToTicket(index)}
            />
          ))}
        </div>

        <div
          className="show-image float-soft"
          style={{ animationDelay: "0.1s" }}
        >
          <Image
            src="/images/star-tickets.png"
            width={280}
            height={90}
            className="absolute top-36"
            alt="Stars"
            priority
          />
        </div>

        <div
          className="show-image float-soft"
          style={{ animationDelay: "0.3s" }}
        >
          <Image
            src="/images/star.png"
            width={200}
            height={90}
            className="absolute top-36 right-10"
            alt="Stars"
            priority
          />
        </div>

        <style jsx>{`
          :global(.animate-scroll) {
            animation: carrosselAnimacao 24s linear infinite;
            will-change: transform;
          }

          @keyframes carrosselAnimacao {
            from {
              transform: translateX(0);
            }
            to {
              transform: translateX(-50%);
            }
          }

          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(14px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes fadeInScale {
            from {
              opacity: 0;
              transform: scale(0.96);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }

          @keyframes floatSoft {
            0%,
            100% {
              transform: translateY(0);
            }
            50% {
              transform: translateY(-8px);
            }
          }

          .reveal-up {
            opacity: 0;
            transform: translateY(14px);
          }

          .is-visible.reveal-up {
            animation: fadeInUp 0.75s cubic-bezier(0.22, 1, 0.36, 1) both;
          }

          .reveal-scale {
            opacity: 0;
            transform: scale(0.96);
          }

          .is-visible.reveal-scale {
            animation: fadeInScale 0.8s cubic-bezier(0.16, 1, 0.3, 1) both;
          }

          .float-soft {
            opacity: 0;
          }

          .is-visible.float-soft {
            animation: floatSoft 5s ease-in-out infinite;
            opacity: 1;
          }

          .tickets {
            background: var(--brand-blue);
            padding: 0rem 0rem;
            // position: relative;
            scroll-margin-top: 6.5rem;
          }

          .tickets-title {
            background: var(--brand-yellow);
            color: #2033ef;
            font-size: 1.9rem;
            // font-weight: bold;
            padding: 1rem 2rem;
            display: inline-block;
            margin: 0 auto 1rem;
            display: block;
            width: fit-content;
          }

          .tickets-subtitle {
            color: white;
            text-align: center;
            margin-bottom: 3rem;
            line-height: 1.8;
          }

          .tickets-container {
            display: flex;
            flex-direction: row;
            gap: 1rem;
            max-width: 55%;
            margin: 0 auto;
            margin-top: 30px;
            // width: 350px;
          }

          .ticket-dots {
            display: none;
          }

          .age-rating-accordion {
            width: calc(100% - 40px);
            max-width: 500px;
            margin-top: 24px;
            border-radius: 22px;
            background: #2033ef;
            color: white;
            overflow: hidden;
            box-shadow: 0 10px 30px rgba(32, 51, 239, 0.2);
          }

          .age-rating-trigger {
            width: 100%;
            min-height: 68px;
            padding: 0 18px 0 18px;
            border: 0;
            background: transparent;
            color: inherit;
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 16px;
            text-align: left;
            font-size: 15px;
            font-weight: 700;
            cursor: pointer;
          }

          .age-rating-icon {
            position: relative;
            width: 30px;
            height: 30px;
            flex-shrink: 0;
          }

          .age-rating-panel {
            display: grid;
            grid-template-rows: 0fr;
            opacity: 0;
            transition:
              grid-template-rows 0.45s ease,
              opacity 0.3s ease;
          }

          .age-rating-panel.is-open {
            grid-template-rows: 1fr;
            opacity: 1;
          }

          .age-rating-panel-inner {
            min-height: 0;
            overflow: hidden;
            padding: 0 18px 0;
          }

          .age-rating-panel.is-open .age-rating-panel-inner {
            padding-bottom: 18px;
          }

          .age-rating-panel-inner p {
            font-size: 14px;
            line-height: 1.2;
          }

          .age-rating-panel-inner p + p {
            margin-top: 14px;
          }

          .age-rating-link {
            text-decoration: underline;
            text-underline-offset: 2px;
          }

          .ticket-dot {
            width: 10px;
            height: 10px;
            border-radius: 999px;
            border: 0;
            background: rgba(32, 51, 239, 0.24);
            transition:
              transform 0.2s ease,
              background-color 0.2s ease;
            cursor: pointer;
          }

          .ticket-dot.is-active {
            background: #2033ef;
            transform: scale(1.1);
          }

          .ticket-card {
            height: 500px;
            width: 100%;
            max-width: 380px;
            border-radius: 20px;
            overflow: hidden;
          }

          .ticket-card-cativa {
            height: 500px;
            width: 100%;
            max-width: 380px;
            border-radius: 20px;
            overflow: hidden;
          }

          .ticket-header {
            padding: 0.4rem;
            color: white;
            text-align: center;
            font-size: 0.9rem;
            font-weight: bold;
          }

          .ticket-header.red {
            background: var(--brand-red);
          }

          .ticket-price {
            display: block;
            font-size: 1.5rem;
            margin: 0.5rem 0;
          }

          .ticket-content {
            padding: 2rem;
          }

          .ticket-content.cyan-bg {
            background: #31b1e1;
            color: white;
          }

          .ticket-type {
            font-size: 2.5rem;
            font-weight: bold;
            margin-bottom: 1rem;
          }

          .ticket-description {
            margin-bottom: 1.5rem;
            font-size: 0.95rem;
          }

          .ticket-badge {
            width: 75%;
            margin-top: 5px;
            color: white;
            padding: 8px;
            border-radius: 4px;
            text-align: left;
            font-weight: bold;
            font-size: 0.85rem;
          }

          .ticket-features {
            list-style: none;
            margin-bottom: 1rem;
          }

          .ticket-features li {
            padding: 0.3rem 0;
            padding-left: 1.5rem;
            position: relative;
          }

          .ticket-features li:before {
            content: "✓";
            position: absolute;
            left: 0;
            font-weight: bold;
          }

          .btn-comprar-ticket {
            width: 100%;
            padding-bottom: 14px;
            padding: 1rem;
            border: none;
            border-radius: 50px;
            font-weight: bold;
            font-size: 1.2rem;
            cursor: pointer;
            transition: transform 0.3s;
          }

          .btn-comprar-ticket.yellow {
            background: var(--brand-yellow);
            color: #2033ef;
          }

          .btn-comprar-ticket:hover {
            transform: scale(1.05);
          }

          .tickets-badge {
            text-align: center;
            margin-top: 3rem;
          }

          .badge-text {
            background: white;
            color: var(--brand-blue);
            padding: 1rem 2rem;
            border-radius: 50px;
            font-weight: bold;
            display: inline-block;
          }

          .carousel-section {
            background: var(--brand-blue);
            padding: 4rem 2rem;
            position: relative;
          }

          .carousel-title {
            background: var(--brand-yellow);
            color: #000;
            font-size: 2rem;
            font-weight: bold;
            padding: 1rem 2rem;
            display: inline-block;
            text-align: center;
            width: 100%;
          }

          .carousel-subtitle {
            color: white;
            text-align: center;
            font-size: 1.5rem;
            margin-bottom: 3rem;
          }

          .carousel {
            max-width: 1000px;
            margin: 0 auto;
            display: flex;
            align-items: center;
            gap: 2rem;
          }

          .carousel-btn {
            background: white;
            border: none;
            width: 50px;
            height: 50px;
            border-radius: 50%;
            font-size: 2rem;
            cursor: pointer;
            transition: transform 0.3s;
            flex-shrink: 0;
          }

          .carousel-btn:hover {
            transform: scale(1.1);
          }

          .carousel-content {
            background: white;
            border-radius: 20px;
            padding: 2rem;
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 2rem;
            align-items: center;
          }

          .slide-image {
            width: 100%;
            height: auto;
            border-radius: 10px;
          }

          .carousel-item-title {
            color: var(--brand-green);
            font-size: 1.8rem;
            font-weight: bold;
            margin-bottom: 1rem;
          }

          .carousel-item-description {
            margin-bottom: 1rem;
            line-height: 1.6;
          }

          .carousel-item-highlight {
            font-style: italic;
            color: #666;
          }

          .carousel-logo {
            text-align: right;
            margin-top: 2rem;
            color: var(--brand-yellow);
            font-weight: bold;
            font-size: 2rem;
            line-height: 1.2;
          }

          .partner-section {
            background: var(--brand-yellow);
            padding: 4rem 2rem;
          }

          .partner-title {
            text-align: center;
            font-size: 2.5rem;
            font-weight: bold;
            margin-bottom: 2rem;
          }

          .pink-text {
            color: var(--brand-pink);
          }

          .partner-description,
          .partner-cta {
            text-align: center;
            max-width: 800px;
            margin: 0 auto 2rem;
            line-height: 1.8;
          }

          .partner-form {
            max-width: 600px;
            margin: 0 auto;
            display: flex;
            flex-direction: column;
            gap: 1rem;
          }

          .form-input {
            padding: 1rem;
            border: none;
            border-radius: 10px;
            font-size: 1rem;
            background: white;
          }

          .form-note {
            font-size: 0.85rem;
            text-align: center;
            font-style: italic;
          }

          .btn-enviar {
            background: var(--brand-blue);
            color: white;
            padding: 1rem 3rem;
            border: none;
            border-radius: 30px;
            font-weight: bold;
            font-size: 1rem;
            cursor: pointer;
            align-self: center;
            transition: transform 0.3s;
          }

          .btn-enviar:hover {
            transform: scale(1.05);
          }

          .partner-contact {
            text-align: center;
            margin-top: 2rem;
          }

          .partner-contact a {
            color: #000;
            font-weight: bold;
          }

          .corporate-section {
            background: var(--brand-green);
            padding: 4rem 2rem;
            position: relative;
          }

          .corporate-title {
            background: white;
            color: var(--brand-blue);
            font-size: 2rem;
            font-weight: bold;
            padding: 1rem 2rem;
            display: inline-block;
            text-align: center;
            width: 100%;
            margin-bottom: 1rem;
          }

          .corporate-subtitle {
            color: white;
            text-align: center;
            margin-bottom: 3rem;
            font-size: 1.1rem;
          }

          .corporate-benefits {
            max-width: 800px;
            margin: 0 auto 3rem;
            display: flex;
            flex-direction: column;
            gap: 1rem;
          }

          .benefit-item {
            padding: 1.5rem;
            border-radius: 50px;
            color: white;
            font-weight: bold;
          }

          .benefit-item.pink {
            background: var(--brand-pink);
          }

          .btn-fale-conosco {
            background: white;
            color: var(--brand-green);
            padding: 1rem 3rem;
            border: none;
            border-radius: 30px;
            font-weight: bold;
            font-size: 1rem;
            cursor: pointer;
            display: block;
            margin: 0 auto;
            transition: transform 0.3s;
          }

          .btn-fale-conosco:hover {
            transform: scale(1.05);
          }

          .corporate-badge {
            text-align: right;
            margin-top: 2rem;
            color: white;
            font-weight: bold;
            font-size: 1.5rem;
          }

          .footer {
            background: var(--brand-blue);
            padding: 4rem 2rem;
            color: white;
          }

          .footer-title {
            text-align: center;
            font-size: 2.5rem;
            font-weight: bold;
            margin-bottom: 2rem;
            color: var(--brand-yellow);
          }

          .footer-content {
            max-width: 1200px;
            margin: 0 auto;
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-wrap: wrap;
            gap: 2rem;
          }

          .footer-links {
            display: flex;
            gap: 1rem;
            align-items: center;
          }

          .footer-links a {
            color: white;
            text-decoration: none;
            font-size: 0.9rem;
          }

          .footer-logos {
            display: flex;
            gap: 1rem;
            align-items: center;
          }

          .footer-social {
            display: flex;
            gap: 1rem;
          }

          .social-link {
            width: 40px;
            height: 40px;
            background: white;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            text-decoration: none;
            transition: transform 0.3s;
          }

          .social-link:hover {
            transform: scale(1.1);
          }

          @media (max-width: 768px) {
            .age-rating-accordion {
              width: calc(100% - 16px);
              margin-top: 20px;
            }

            .age-rating-trigger {
              min-height: 70px;
              padding-right: 16px;
            }

            .age-rating-panel-inner {
              padding-left: 16px;
              padding-right: 16px;
            }

            .tickets-container {
              display: flex;
              flex-direction: row;
              width: 90%;
              max-width: 100%;
              overflow-x: auto;
              overflow-y: hidden;
              scroll-snap-type: x mandatory;
              scroll-behavior: smooth;
              gap: 0.75rem;
              padding-bottom: 0.5rem !important;
              -ms-overflow-style: none;
              scrollbar-width: none;
            }

            .tickets-container::-webkit-scrollbar {
              display: none;
            }

            .ticket-card {
              height: auto;
              width: 100%;
              min-width: 100%;
              flex: 0 0 100%;
              border-radius: 20px;
              overflow: hidden;
              scroll-snap-align: start;
            }

            .ticket-card-cativa {
              height: auto;
              width: 100%;
              min-width: 100%;
              flex: 0 0 100%;
              border-radius: 20px;
              overflow: hidden;
              scroll-snap-align: start;
            }

            .ticket-dots {
              display: flex;
              justify-content: center;
              gap: 0.6rem;
              margin-top: -2rem;
              margin-bottom: 2rem;
            }

            .show-image {
              display: none;
            }
          }

          @media (prefers-reduced-motion: reduce) {
            :global(.animate-scroll) {
              animation: none;
              transform: translateX(0);
            }
          }
        `}</style>
      </section>
    </>
  );
}
